import { useTranslation } from 'react-i18next'
import type { UsageBreakdownRow } from '../lib/types'
import { SINGLE_SERIES_COLOR } from '../lib/invocationTypes'

/** One `.ubar-row`: a label, a bar, and a bare total.
 *
 * No "/ limit" fraction and no percentage: `max` is the *largest bar in this
 * group*, not an entitlement, because there is no per-tenant or per-user
 * limit to measure a share against. Those only exist at account level (the
 * KPI tile) and per user (the User Limits page).
 *
 * One series on both products, in `--inv-auth`, with no legend (MS-996; the
 * prototype's `barHTML`): the invocation type is not split out because
 * pricing does not differentiate by type. */
export function UsageBarRow({
  label,
  sub,
  total,
  max,
}: Readonly<{
  label: string
  sub?: string
  total: number
  max: number
}>) {
  const { i18n } = useTranslation()
  const rowWidth = max > 0 ? Math.min(100, (total / max) * 100) : 0

  return (
    <div className="flex items-center gap-3 py-2 border-b border-[var(--border-light)] last:border-b-0">
      <div className="w-[190px] min-w-[190px] overflow-hidden">
        <div className="text-[12.5px] text-[var(--text-primary)] truncate" title={label}>
          {label}
        </div>
        {sub && <div className="text-[11px] text-[var(--text-tertiary)] truncate">{sub}</div>}
      </div>
      <div className="flex-1 h-2 rounded flex overflow-hidden bg-[rgba(128,128,128,0.16)]">
        {total > 0 && (
          <div data-testid="ubar-seg" className="h-full" style={{ width: `${rowWidth}%`, background: SINGLE_SERIES_COLOR }} />
        )}
      </div>
      <div className="w-[120px] min-w-[120px] text-right text-[12.5px] font-semibold tabular-nums text-[var(--text-primary)]">
        {total.toLocaleString(i18n.language)}
      </div>
    </div>
  )
}

/** The rows of a By-tenant / By-user card, scaled to their own group's
 * largest bar (the prototype's `tMax`/`uMax`). */
export function UsageBreakdownList({
  rows,
  emptyText,
  fallbackLabel,
}: Readonly<{
  rows: UsageBreakdownRow[]
  emptyText: string
  fallbackLabel: string
}>) {
  if (rows.length === 0) {
    return <div className="text-[12.5px] text-[var(--text-tertiary)] py-3.5">{emptyText}</div>
  }
  const max = Math.max(1, ...rows.map((r) => r.total))
  return (
    <>
      {rows.map((row) => (
        <UsageBarRow
          key={row.id}
          label={row.label ?? fallbackLabel}
          total={row.total}
          max={max}
        />
      ))}
    </>
  )
}
